<template>
  <div v-if="isLoading" class="center-all cloudinary">
    <v-progress-circular size="50" color="primary" indeterminate />
  </div>
  <div v-else-if="errorLoading" class="center-all cloudinary">
    <v-icon>{{ type === 'video' ? 'mdi-video-off' : 'mdi-image' }}</v-icon>
  </div>
  <img
    v-else-if="type === 'image' && cloudinaryImage"
    :src="cloudinaryImage.toURL()"
    alt=""
    @error="markError"
  />
  <img
    v-else-if="type === 'video' && snapshot && cloudinaryVideo"
    :src="cloudinaryVideo.toURL()"
    alt=""
    @error="markError"
  />
  <video
    v-else-if="type === 'video' && cloudinaryVideo"
    :src="cloudinaryVideo.toURL()"
    controls
    playsinline
    preload="metadata"
    @error="markError"
  />
</template>

<script setup lang="ts">
import { fill } from '@cloudinary/url-gen/actions/resize'
import {
  Cloudinary,
  CloudinaryImage,
  CloudinaryVideo,
} from '@cloudinary/url-gen'
import { getCloudinaryPublicId, getCloudName } from './helper'

const cloudFolder = import.meta.env.VITE_APP_CLOUD_FOLDER

const { src, type, width, height, snapshot } = defineProps({
  src: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'image',
  },
  snapshot: {
    type: Boolean,
    default: false,
  },
  width: {
    type: [String, Number],
    default: 250,
  },
  height: {
    type: [String, Number],
    default: 250,
  },
})

const currentCloudName = computed(() => getCloudName(src))
const cld = computed(
  () =>
    new Cloudinary({
      cloud: {
        cloudName: currentCloudName.value,
      },
    })
)

const computedWidth = computed(() => `${width}px`)
const computedHeight = computed(() => `${height}px`)
function preloadImages(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = url
    img.onload = resolve
    img.onerror = reject
  })
}

const isLoading = ref(true)
const errorLoading = ref(false)
const markError = () => {
  isLoading.value = false
  errorLoading.value = true
}
const publicId = computed<string | null>(() =>
  getCloudinaryPublicId(src || '', cloudFolder)
)
const cloudinaryImage = ref<CloudinaryImage | null>(null)
const cloudinaryVideo = ref<CloudinaryVideo | null>(null)

// onMounted(async () => {
// image logic
const loadImage = async () => {
  isLoading.value = true
  errorLoading.value = false
  if (!publicId.value) {
    markError()
    return
  }
  try {
    const image = cld.value.image(publicId.value)
    image.resize(fill().width(width).height(height))
    image.format('webp').quality('auto')
    cloudinaryImage.value = image
    await preloadImages(cloudinaryImage.value.toURL())
    isLoading.value = false
  } catch (e) {
    onError(e)
  }
}

// video logic
const loadVideo = async () => {
  isLoading.value = true
  errorLoading.value = false
  if (!publicId.value) {
    markError()
    return
  }
  try {
    const video = cld.value.video(publicId.value)
    video.resize(fill().width(width).height(height))
    // A gallery tile needs a still frame. The full player keeps the MP4.
    video.format(snapshot ? 'jpg' : 'mp4')
    cloudinaryVideo.value = video
    if (snapshot) await preloadImages(cloudinaryVideo.value.toURL())
    isLoading.value = false
  } catch (e) {
    onError(e)
  }
}
const loadMedia = () => {
  if (type === 'image') {
    loadImage()
  } else {
    loadVideo()
  }
}
watch(
  [publicId, () => type, () => snapshot],
  () => {
    loadMedia()
  },
  { immediate: true }
)
const onError = (error: any) => {
  console.log(error)
  markError()
}
onMounted(() => {
  loadMedia()
})
</script>
<style lang="scss" scoped>
.cloudinary {
  width: v-bind(computedWidth) !important;
  height: v-bind(computedHeight) !important;
}

img,
video {
  width: 100%;
  display: block;
  object-fit: cover;
  background: #111;
}

video {
  height: 100%;
  object-fit: contain;
}
</style>
